import { useCallback, useEffect, useState } from 'react';
import { nuclearReload } from './nuclearReload';
import { fetchServerBuildId, isNewerBuild } from './version';

/** Chemin du service worker généré au build (cf. plugin `serviceWorkerPlugin`). */
const SW_URL = '/sw.js';

/** Périodicité du sondage du `buildId` serveur (ms). */
const POLL_INTERVAL_MS = 60_000;

/**
 * Intervalle minimal entre deux vérifications déclenchées par un RETOUR D'ONGLET.
 * Sans ce plancher, chaque `visibilitychange` sonde le serveur — un aller-retour
 * entre deux onglets suffit à multiplier les requêtes.
 */
const VISIBILITY_THROTTLE_MS = 30_000;

interface AppUpdateState {
  /** `true` dès qu'une nouvelle version est détectée (SW en attente OU buildId serveur différent). */
  updateAvailable: boolean;
  /** Déclenche le rechargement nucléaire (purge caches + SW puis reload). */
  reload: () => Promise<void>;
}

/**
 * Détection de nouvelle version + enregistrement du service worker.
 *
 * Deux signaux convergent sur `updateAvailable` :
 *  1. **SW** — un nouveau worker atteint l'état `installed`/`waiting` (nouvel asset déployé).
 *  2. **buildId** — le `buildId` de `/api/health` diffère de celui embarqué au build
 *     (sondage périodique + au retour de focus/visibilité).
 *
 * **Inerte en dev et en test** : aucun enregistrement de SW, aucun sondage, hors
 * `import.meta.env.PROD` ou si `navigator.serviceWorker` est absent (happy-dom).
 */
export function useAppUpdate(): AppUpdateState {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Garde dev/test : pas de SW ni de sondage hors production navigateur.
    if (!import.meta.env.PROD) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    let cancelled = false;
    // `true` dès qu'une mise à jour est détectée : ARRÊTE tout sondage ultérieur.
    // Le bandeau ne peut plus changer d'état — continuer à sonder ne produirait
    // que des requêtes sans effet (un onglet laissé ouvert 8 h après un déploiement
    // en émettait ~480).
    let detected = false;
    // Verrou d'appel EN VOL : un `visibilitychange` tombant pendant un tick du
    // sondage lançait sinon deux requêtes concurrentes.
    let inFlight = false;
    let lastVisibilityCheck = 0;
    const controller = new AbortController();
    const flag = (): void => {
      detected = true;
      if (!cancelled) setUpdateAvailable(true);
    };

    // 1) Service worker : enregistrement + détection d'un worker en attente.
    void navigator.serviceWorker
      .register(SW_URL)
      .then((registration) => {
        if (registration.waiting) flag();
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `installed` + un contrôleur déjà présent ⇒ mise à jour prête.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) flag();
          });
        });
      })
      .catch(() => {
        /* enregistrement best-effort : le signal buildId prend le relais */
      });

    // 2) buildId : sondage périodique + au retour de focus/visibilité.
    // Déclaré AVANT `checkBuild` (qui l'arrête à la détection) pour éviter toute
    // zone morte temporelle.
    let interval = 0;
    const checkBuild = async (): Promise<void> => {
      if (cancelled || detected || inFlight) return;
      inFlight = true;
      try {
        const serverBuildId = await fetchServerBuildId(controller.signal);
        if (isNewerBuild(serverBuildId)) {
          flag();
          window.clearInterval(interval); // rien à détecter de plus : on sort du sondage
        }
      } finally {
        inFlight = false;
      }
    };
    void checkBuild();
    interval = window.setInterval(() => void checkBuild(), POLL_INTERVAL_MS);
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibilityCheck < VISIBILITY_THROTTLE_MS) return;
      lastVisibilityCheck = now;
      void checkBuild();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const reload = useCallback(() => nuclearReload(), []);

  return { updateAvailable, reload };
}
